/**
 * Channel Console — Product module (A7Y-3778).
 *
 * Two data sources feed one funnel, and the module says so out loud rather
 * than blending them: "Signed up" / "Activated" / "Ran an audit" are
 * Mixpanel-sourced (per-day BQ history, same nightly-pull architecture as the
 * Website module); "Started a trial" / "Converted to paid" are LIVE reads of
 * the Firestore org ledger on every GET (D13) — the same collection the MRR
 * tile reads, so the funnel's paid stages and the revenue tiles reconcile by
 * construction. Revenue (MRR, new revenue, churn, active/trialing subs) is
 * likewise a live read, independent of whether Mixpanel is configured.
 *
 * Because the two funnel halves are independent, this module never does one
 * Website-style early return that hides everything. Each axis of possible
 * failure gets its own explicit, additive state:
 *
 *   - `funnel.configured === false` — the Mixpanel Worker secrets are absent
 *     AND no BQ history exists. Stages 1-3 would otherwise render as a
 *     confident wall of zeros, so they are replaced by a notice; revenue and
 *     the two ledger-sourced stages are unaffected and still render.
 *   - `funnel.trend.length === 0` (configured, but nothing aggregated yet) —
 *     same treatment as the Website module's empty-window state, split on
 *     `freshness.lastError`; again only stages 1-3 are affected.
 *   - `funnel.complete === false` — part of the window isn't aggregated yet;
 *     additive partial-window notice, stages 1-3 still show partial totals
 *     (mirrors the Website module).
 *   - `funnel.freshness.stale` — additive stale banner, never replacing
 *     content (mirrors the Website module).
 *   - `funnel.ledgerStagesError` — the ledger read failed; stages 4-5 render
 *     as unavailable WITHOUT hiding stages 1-3.
 *   - `revenueError` — the revenue read failed; a notice replaces the tile
 *     strip, the funnel is unaffected.
 *
 * Per-stage loud badge (Mixpanel stages only): `count === 0 && unkeyed > 0`
 * means the stage's event matched real traffic but every match failed to key
 * to an actor — the same "wrong event name" failure class the Website module
 * warns about, just caught per-stage instead of module-wide, because Product
 * has three independent event names rather than one.
 *
 * The Signed-up -> Activated rate is a users -> orgs ratio (D1 — an invited
 * teammate fires `Signup Completed` but never `Org Created`), so it is
 * visually marked as a cross-unit ratio rather than presented as a plain
 * conversion rate like the other stage-to-stage rates.
 */
import type { ChannelConsoleProductData, ProductFunnelStagePayload } from './types';
import { Card, Notice, formatInt, formatLocal, formatPercent } from './ui';

function formatUsd(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

/** Small uppercase pill, used for source/unit/cross-unit labeling on a stage card. */
function Tag({ tone, children }: { tone: 'neutral' | 'warn'; children: React.ReactNode }) {
  const palette =
    tone === 'warn'
      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300'
      : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${palette}`}
    >
      {children}
    </span>
  );
}

/**
 * One stage of the funnel. `crossUnitRate` marks the stage-1→2 rate as a
 * ratio between two different id spaces (D1/D15) rather than a same-space
 * conversion rate. `unavailable` covers both "not configured yet" reasons
 * (Mixpanel not pulled, or the ledger read failed) — the card shows a dash
 * and says why instead of a number.
 */
function FunnelStageCard({
  stage,
  index,
  crossUnitRate,
  unavailableReason,
  subtext,
}: {
  stage: ProductFunnelStagePayload;
  index: number;
  crossUnitRate: boolean;
  unavailableReason: string | null;
  subtext: string | null;
}) {
  const loudZeroKeyed = stage.source === 'mixpanel' && stage.count === 0 && stage.unkeyed > 0;

  return (
    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800 text-[10px] font-semibold text-zinc-500">
              {index + 1}
            </span>
            <h3 className="text-xs uppercase tracking-wide text-zinc-500">{stage.label}</h3>
          </div>
          <div className="mt-1 text-2xl font-semibold text-zinc-900 dark:text-zinc-50">
            {unavailableReason ? '—' : formatInt(stage.count ?? 0)}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Tag tone="neutral">{stage.unit}</Tag>
            {stage.source === 'ledger' && <Tag tone="neutral">From billing ledger</Tag>}
            {crossUnitRate && !unavailableReason && <Tag tone="neutral">Cross-unit ratio</Tag>}
            {loudZeroKeyed && <Tag tone="warn">Matched events, 0 keyed</Tag>}
          </div>
          {subtext && <div className="mt-1 text-xs text-zinc-500">{subtext}</div>}
          {unavailableReason && (
            <div className="mt-1 text-xs text-red-700 dark:text-red-400">{unavailableReason}</div>
          )}
          {!unavailableReason && stage.rate !== null && (
            <div className="mt-1 text-xs text-zinc-500">
              {formatPercent(stage.rate)} of previous stage
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProductModule({
  data,
  isLoading,
  error,
}: {
  data: ChannelConsoleProductData | undefined;
  isLoading: boolean;
  error: string | null;
}): React.JSX.Element {
  if (error) {
    return (
      <Notice tone="warn" title="Failed to load the product module">
        {error}
      </Notice>
    );
  }

  if (isLoading && !data) {
    return <div className="text-sm text-zinc-500">Loading product data…</div>;
  }

  if (!data) return <></>;

  const { funnel, revenue, revenueError } = data;

  // Mixpanel stages (1-3) render numeric cards only once there is real
  // aggregated history to show — otherwise every one of them would read as a
  // confident zero. The two ledger stages and the revenue tiles below never
  // depend on this: they are live reads, unaffected by whether Mixpanel has
  // been configured or pulled yet.
  const mixpanelUnconfigured = !funnel.configured;
  const mixpanelWindowEmpty = funnel.configured && funnel.trend.length === 0;
  const mixpanelStagesHidden = mixpanelUnconfigured || mixpanelWindowEmpty;

  // "tracked since" applies whenever the queried window starts before the
  // date the central `trialConvertedAt` stamp shipped — `trend` is the only
  // signal of window start carried in this payload (oldest aggregated day
  // first); an empty trend means nothing is known to contradict the label,
  // so it is shown by default rather than silently suppressed.
  const windowStartsBeforeTracking =
    funnel.trend.length === 0 || funnel.trend[0].date < funnel.conversionTrackedSince;

  return (
    <div className="space-y-6">
      {funnel.freshness.stale && (
        <Notice tone="warn" title="Data is stale">
          {funnel.freshness.lastSuccessAt === null
            ? 'No successful pull yet.'
            : `Last successful pull ${formatLocal(funnel.freshness.lastSuccessAt)}.`}
          {funnel.freshness.lastError && (
            <div className="mt-1 font-mono text-xs text-red-700 dark:text-red-400">
              {funnel.freshness.lastError}
            </div>
          )}
        </Notice>
      )}

      {/* Revenue — independent of the funnel's Mixpanel state. */}
      {revenueError ? (
        <Notice tone="warn" title="Revenue unavailable">
          {revenueError}
        </Notice>
      ) : revenue ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card
              title="MRR (entitlements)"
              primary={formatUsd(revenue.mrr)}
              secondary="Active paid plans in org ledgers — not Stripe billing"
            />
            <Card
              title="New revenue (7d)"
              primary={formatUsd(revenue.newRevenue7d)}
              secondary="Net USD cash"
            />
            <Card
              title="Churn (30d)"
              primary={formatPercent(revenue.churnRate30d)}
              secondary={`${formatInt(revenue.churnCanceled30d)} canceled / ${formatInt(revenue.churnActiveBase)} base`}
            />
            <Card
              title="Active paid plans"
              primary={formatInt(revenue.activeSubscriptions)}
              secondary={`${formatInt(revenue.trialingSubscriptions)} trialing — ledger entitlements, not Stripe`}
            />
          </div>

          {revenue.mrrByTier.length > 0 && (
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                MRR by plan tier
              </h3>
              <p className="mt-0.5 text-xs text-zinc-500">
                From org plan ledgers (active paid entitlements) — not Stripe billing, which the
                New revenue and Churn tiles read.
              </p>
              <table className="mt-4 w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
                    <th className="pb-2 font-medium">Tier</th>
                    <th className="pb-2 text-right font-medium">MRR</th>
                    <th className="pb-2 text-right font-medium">Subscriptions</th>
                  </tr>
                </thead>
                <tbody>
                  {revenue.mrrByTier.map((row) => (
                    <tr key={row.tier} className="border-t border-zinc-100 dark:border-zinc-800">
                      <td className="py-1.5 font-mono text-xs text-zinc-700 dark:text-zinc-300">
                        {row.tier}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-zinc-900 dark:text-zinc-100">
                        {formatUsd(row.mrr)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-zinc-900 dark:text-zinc-100">
                        {formatInt(row.count)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {/* Funnel. */}
      <div className="space-y-4">
        {mixpanelUnconfigured && (
          <Notice tone="warn" title="Product funnel not configured">
            The Mixpanel read credentials are not set as Worker secrets in this environment, so
            signup/activation/audit stages can't be fetched. The two ledger-sourced stages below are
            unaffected — see A7Y-3800.
          </Notice>
        )}

        {mixpanelWindowEmpty &&
          (funnel.freshness.lastError === null ? (
            <Notice tone="info" title="Window not pulled yet">
              No days are aggregated for the signup/activation/audit stages. The nightly pull fills
              the window on its own; Refresh pulls the three most recent missing days now.
            </Notice>
          ) : (
            <Notice tone="warn" title="Window not aggregated">
              No days are aggregated and the last pull failed. Refresh retries now.
              <div className="mt-1 font-mono text-xs text-red-700 dark:text-red-400">
                {funnel.freshness.lastError}
              </div>
            </Notice>
          ))}

        {!mixpanelStagesHidden && !funnel.complete && (
          <Notice tone="info" title="Partial window">
            {funnel.daysMissing.length} of the last 30 days are not aggregated yet, so the
            signup/activation/audit totals below are partial. The nightly pull closes the gap over
            the following nights; Refresh pulls the three most recent missing days now.
          </Notice>
        )}

        {funnel.ledgerStagesError && (
          <Notice tone="warn" title="Trial/conversion stages unavailable">
            The billing-ledger read failed, so "Started a trial" and "Converted to paid" can't be
            shown this load.
            <div className="mt-1 font-mono text-xs text-red-700 dark:text-red-400">
              {funnel.ledgerStagesError}
            </div>
          </Notice>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {funnel.stages.map((stage, index) => {
            if (stage.source === 'mixpanel' && mixpanelStagesHidden) return null;

            const previous = funnel.stages[index - 1];
            const crossUnitRate = Boolean(previous && previous.unit !== stage.unit);
            // A rate is only meaningful when the stage it compares against was
            // actually rendered above — the fixed order means "previous" is
            // hidden exactly when it's a Mixpanel stage under
            // `mixpanelStagesHidden`.
            const previousRendered =
              !previous || !(previous.source === 'mixpanel' && mixpanelStagesHidden);
            const effectiveStage = previousRendered ? stage : { ...stage, rate: null };

            const unavailableReason =
              stage.source === 'ledger' && funnel.ledgerStagesError
                ? 'Unavailable — ledger read failed'
                : null;

            let subtext: string | null = null;
            if (stage.key === 'activated') subtext = 'New organizations created';
            if (stage.key === 'converted' && windowStartsBeforeTracking) {
              subtext = `Tracked since ${funnel.conversionTrackedSince}`;
            }

            return (
              <FunnelStageCard
                key={stage.key}
                stage={effectiveStage}
                index={index}
                crossUnitRate={crossUnitRate}
                unavailableReason={unavailableReason}
                subtext={subtext}
              />
            );
          })}
        </div>

        {!mixpanelStagesHidden && (
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                Daily signups
              </h3>
              <p className="mt-0.5 text-xs text-zinc-500">
                Last {funnel.trend.length} complete {funnel.trend.length === 1 ? 'day' : 'days'},
                UTC
              </p>
              <div
                className="mt-4 flex h-40 items-end gap-px"
                role="img"
                aria-label="Daily signups"
              >
                {(() => {
                  const maxSignups = Math.max(1, ...funnel.trend.map((p) => p.signups));
                  return funnel.trend.map((point) => (
                    <div
                      key={point.date}
                      className="flex-1 rounded-t bg-blue-500/80 dark:bg-blue-400/80"
                      style={{ height: `${Math.max(2, (point.signups / maxSignups) * 100)}%` }}
                      title={`${point.date}: ${formatInt(point.signups)} signups`}
                    />
                  ));
                })()}
              </div>
            </div>

            <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-4">
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                Signups by source
              </h3>
              <p className="mt-0.5 text-xs text-zinc-500">Same-day first-touch attribution</p>
              {funnel.signupsBySource.length === 0 ? (
                <p className="mt-4 text-sm text-zinc-500">No signups in this window.</p>
              ) : (
                <table className="mt-4 w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
                      <th className="pb-2 font-medium">Source</th>
                      <th className="pb-2 text-right font-medium">Signups</th>
                    </tr>
                  </thead>
                  <tbody>
                    {funnel.signupsBySource.map((row) => (
                      <tr
                        key={row.source}
                        className="border-t border-zinc-100 dark:border-zinc-800"
                      >
                        <td className="py-1.5 font-mono text-xs text-zinc-700 dark:text-zinc-300">
                          {row.source}
                        </td>
                        <td className="py-1.5 text-right tabular-nums text-zinc-900 dark:text-zinc-100">
                          {formatInt(row.signups)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
